/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  filterRespondItemsByMitra,
  getLastOwnEditableResponse,
  hasClauseResponse,
  mapResponsesByTypeResponse,
  normalizeRespondOperationalToleranceValidity,
  sortRespondOperationalToleranceValidities,
  stampSavedResponse,
  toClauseResponseList,
  unwrapRespondListItems,
  TYPE_RESPONSE_TOLERANCE,
  TYPE_RESPONSE_VALIDITY,
  TYPE_RESPONSE_DOCUMENT,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  TYPE_RESPONSE_PRODUCT_SPECIFICATION,
} from './helpers';
import { buildRespondTypeResponseListUrl } from './listParams';
import {
  lockRespondStatus,
  LOCK_RESOURCE_TYPE_RESPONSE,
} from './lockRespondStatus';
import { unwrapPayload } from '../../api/unwrap';

const EMPTY_RESPONSES = {
  [TYPE_RESPONSE_TOLERANCE]: [],
  [TYPE_RESPONSE_VALIDITY]: [],
  [TYPE_RESPONSE_DOCUMENT]: [],
  [TYPE_RESPONSE_GLOBAL]: [],
  [TYPE_RESPONSE_COUNTER_PAYMENT_TERM]: [],
  [TYPE_RESPONSE_COUNTER_COMPLIANCE]: [],
  [TYPE_RESPONSE_PRODUCT_SPECIFICATION]: [],
};

const parseResponseItems = payload => {
  const list = unwrapRespondListItems(payload)
    .map(normalizeRespondOperationalToleranceValidity)
    .filter(Boolean);

  if (list.length > 0) return list;

  const single = normalizeRespondOperationalToleranceValidity(
    unwrapPayload(payload)
  );
  return single ? [single] : [];
};

const buildItemUrl = id =>
  `${api.API_URL}/respond-operational-tolerance-validity-document/${id}`;

const upsertResponseList = (list, saved) =>
  sortRespondOperationalToleranceValidities([
    ...toClauseResponseList(list).filter(
      item => String(item.id) !== String(saved.id)
    ),
    saved,
  ]);

const useClarificationToleranceValidityResponses = (
  authToken = null,
  procurementId = null,
  procurementMitraId = null,
  isEvaluatorView = true
) => {
  const [responses, setResponses] = useState(EMPTY_RESPONSES);
  const [isLoading, setIsLoading] = useState(Boolean(procurementId));
  const [isSaving, setIsSaving] = useState(false);
  const fetchSeqRef = useRef(0);

  const fetchResponses = useCallback(async () => {
    const token = authToken || getAuthToken();
    if (!token || !procurementId) {
      setResponses(EMPTY_RESPONSES);
      setIsLoading(false);
      return;
    }

    if (!procurementMitraId) {
      setResponses(EMPTY_RESPONSES);
      setIsLoading(false);
      return;
    }

    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setIsLoading(true);
    try {
      const response = await axios.get(
        buildRespondTypeResponseListUrl({
          procurementId,
          procurementMitraId,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (fetchSeqRef.current !== seq) return;

      const items = filterRespondItemsByMitra(
        parseResponseItems(response),
        procurementMitraId,
        { scopedQuery: true }
      );
      setResponses(mapResponsesByTypeResponse(items));
    } catch (error) {
      if (fetchSeqRef.current !== seq) return;
      if (error?.response?.status === 404) {
        setResponses(EMPTY_RESPONSES);
        return;
      }
      console.error('Error fetching tolerance/validity responses:', error);
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [authToken, procurementId, procurementMitraId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const getById = useCallback(
    async id => {
      const token = authToken || getAuthToken();
      if (!token || !id) return null;

      try {
        const response = await axios.get(buildItemUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });

        return (
          normalizeRespondOperationalToleranceValidity(
            unwrapPayload(response)
          ) ?? null
        );
      } catch (error) {
        if (error?.response?.status === 404) return null;
        console.error(
          'Error fetching tolerance/validity response by id:',
          error
        );
        throw error;
      }
    },
    [authToken]
  );

  const saveResponse = useCallback(
    async (typeResponse, respondNotes) => {
      const token = authToken || getAuthToken();
      const notes = String(respondNotes ?? '').trim();
      if (
        !token ||
        !procurementId ||
        !procurementMitraId ||
        !typeResponse ||
        !hasClauseResponse(notes)
      ) {
        return null;
      }

      const existing = getLastOwnEditableResponse(responses[typeResponse], {
        isEvaluator: isEvaluatorView,
      });
      const payload = {
        procurementId,
        ...(isEvaluatorView ? { procurementMitraId } : {}),
        respondNotes: notes,
        typeResponse: Number(typeResponse),
      };

      setIsSaving(true);
      try {
        const response = existing?.id
          ? await axios.put(buildItemUrl(existing.id), payload, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            })
          : await axios.post(
              `${api.API_URL}/respond-operational-tolerance-validity-document`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );

        const saved = stampSavedResponse(
          normalizeRespondOperationalToleranceValidity(unwrapPayload(response)),
          { isEvaluator: isEvaluatorView }
        );

        if (saved) {
          setResponses(prev => ({
            ...prev,
            [saved.typeResponse]: upsertResponseList(
              prev[saved.typeResponse],
              saved
            ),
          }));
          return saved;
        }

        await fetchResponses();
        return null;
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        console.error('Error saving tolerance/validity response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [
      authToken,
      fetchResponses,
      isEvaluatorView,
      procurementId,
      procurementMitraId,
      responses,
    ]
  );

  const deleteResponse = useCallback(
    async typeResponse => {
      const token = authToken || getAuthToken();
      const existing = getLastOwnEditableResponse(responses[typeResponse], {
        isEvaluator: isEvaluatorView,
      });

      if (!token || !existing?.id) {
        return;
      }

      setIsSaving(true);
      try {
        await axios.delete(buildItemUrl(existing.id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setResponses(prev => ({
          ...prev,
          [typeResponse]: toClauseResponseList(prev[typeResponse]).filter(
            item => String(item.id) !== String(existing.id)
          ),
        }));
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        console.error('Error deleting tolerance/validity response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, fetchResponses, isEvaluatorView, responses]
  );

  const lockResponse = useCallback(
    async (responseId, isLocked = true, finalValue = null) => {
      const token = authToken || getAuthToken();
      if (!token || !responseId) return;
      const nextFinalValue = isLocked === true ? (finalValue ?? null) : null;

      setIsSaving(true);
      try {
        await lockRespondStatus({
          token,
          resource: LOCK_RESOURCE_TYPE_RESPONSE,
          id: responseId,
          isLocked,
          finalValue: nextFinalValue,
        });
        setResponses(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(key => {
            next[key] = toClauseResponseList(next[key]).map(item =>
              String(item.id) === String(responseId)
                ? {
                    ...item,
                    isLocked: isLocked === true,
                    finalValue: nextFinalValue,
                  }
                : item
            );
          });
          return next;
        });
        // Choice lock: server fills finalValue (master label) — reload it.
        if (isLocked === true && nextFinalValue == null) await fetchResponses();
      } catch (error) {
        console.error('Error locking tolerance/validity response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, fetchResponses]
  );

  // Agree & Lock satu field kelompok (type 8/9/10/12/13) — baris agreement,
  // bukan thread. Final = label counter (diisi server).
  const agreeField = useCallback(
    // finalValue hanya untuk free-text (19-21); 8-18 diabaikan BE.
    async (typeResponse, isLocked = true, finalValue = undefined) => {
      const token = authToken || getAuthToken();
      if (!token || !procurementId || !procurementMitraId) return;
      setIsSaving(true);
      try {
        await axios.put(
          `${api.API_URL}/respond-operational-tolerance-validity-document/agree-field`,
          {
            procurementId,
            procurementMitraId,
            typeResponse,
            isLocked,
            finalValue,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        await fetchResponses();
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, fetchResponses, procurementId, procurementMitraId]
  );

  return {
    agreeField,
    responses,
    isLoading,
    isSaving,
    refresh: fetchResponses,
    getById,
    saveResponse,
    deleteResponse,
    lockResponse,
  };
};

export default useClarificationToleranceValidityResponses;
